import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { AirportModel } from 'app/airport/models/airport.model';
import { API_ROOT } from '@shared/constants/api-root.const';
import { SessionService } from '@core/config/session';
import { Observable, catchError, throwError } from 'rxjs';

export interface V2AirportPage {
  results: AirportModel[];
  count: number;
}

/**
 * One search for both v2 apps.
 *
 * The briefing and the estimator hit the same endpoint today by two different
 * routes -- SearchPageApiService and CostEstimatorApiService.searchAirports-
 * Paginated both GET {API_ROOT}airport/ with { search, page, page_size }.
 * v2 has one caller of it.
 */
@Injectable({ providedIn: 'root' })
export class V2AirportSearchService {
  private readonly http = inject(HttpClient);
  private readonly apiRoot = inject(API_ROOT);
  private readonly session = inject(SessionService);

  search(params: { page: number; page_size: number; search?: string }): Observable<V2AirportPage> {
    let httpParams = new HttpParams()
      .set('page', params.page.toString())
      .set('page_size', params.page_size.toString());

    if (params.search) { httpParams = httpParams.set('search', params.search); }

    // `PublicAirportViewSet` is paginated with the same paginator as the
    // authenticated search, so both endpoints answer in the same shape and
    // honour the same page_size. It used to set `pagination_class = None`,
    // which meant page/page_size were decoration and the dropdown's opening
    // search -- no term at all -- returned every published airport in one
    // payload.
    const publicSearch = () =>
      this.http.get<V2AirportPage>(`${this.apiRoot}public-airport/`, { params: httpParams });

    if (!this.session.hasSession()) {
      return publicSearch();
    }

    // A ten-year cookie around a token that expires in hours means
    // `hasSession()` can say yes when the server says no. On a refusal the
    // search falls back to the public endpoint rather than leaving a
    // signed-out visitor with a dead type-ahead; anything else surfaces as
    // itself.
    return this.http.get<V2AirportPage>(`${this.apiRoot}airport/`, { params: httpParams }).pipe(
      catchError(error =>
        error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)
          ? publicSearch()
          : throwError(() => error),
      ),
    );
  }
}
